import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import * as Yup from 'yup';
import { Box } from '@/shared/ui';
import { selectCreditCards, selectOnboardingUser, selectUserProfileInfo } from '@/store/slices/onboarding/selectors';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { CustomersController, CustomersStepTypeEnum, CustomersStepTypeEnumPaths, IPrescriptionCopayQuantity } from '@/shared/endpoints/customers';
import { toast } from 'react-toastify';
import { ERROR_MESSAGES } from '@/shared/constants/error-messages';
import { URLSearchParamsInit, useNavigate, useLocation } from 'react-router-dom';
import _, { cloneDeep } from 'lodash';
import { useTheme } from '@mui/material/styles';
import { ROUTES } from '@/shared/constants/routes';
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import { GtmEvent } from '@/shared/types/enums';
import saleForceWebHookInstance from '@/services/saleforce';
import { withScrollRestoration } from '@/shared/hocs/withScrollRestoration';
import { _SplitLayout } from '@/modules/onboarding/components/split-layout';
import { completeOnboardingNotesRequest, cropBase64ImageWithLine } from './helpers';
import { convertedMedications } from '@/shared/helpers/main';
import { paymentFormInitialValues } from '@/shared/components/payment-form/constants';
import ReviewSubmit from './components/submit';
import ReviewMedicationList from './components/medication-list';
import ReviewMainInfo from './components/main-info';
import { uploadSignature } from '../insurance/components/upload-form/helpers';
import { usePostHog } from '@/shared/hooks/usePostHog';
import { useFormikWrapper } from '@/shared/hooks/formikWrapper';
import ReviewPayment from './components/payment';
import { ReviewCompleteForm } from '@/shared/components/payment-form/constants';
import { hueOptionsList } from '@/shared/constants/hue-options';
import { canRunMoreClaims, runFullCopayClaims, ITestClaimSlot, isCopayRequestLimitReached, getPriceEligibleOtc, getOtcCostsByQuantity } from '@/shared/helpers/test-claims-combo';
import { getCopayEligiblePrescriptions } from '@/shared/helpers/eligibility';
import { ITestClaimTransferPrescriptions } from '@/shared/types/interfaces/transfer-prescriptions';

// Convert slot-keyed map (medid:qty:days) to medid-keyed map, picking the slot that matches
// the quantity/daysSupply currently configured for each medication.
// hasMissingCopayAmount is true when any eligible prescription has no matching slot in the result.
const slotsByMedid = (
    slots: Record<string, ITestClaimSlot>,
    eligiblePrescriptions: ITestClaimTransferPrescriptions[],
    prescriptionCopayQuantities?: Record<string, { quantity: number; daysSupply: number }>
): { collectedClaims: Record<string, ITestClaimSlot>; hasMissingCopayAmount: boolean } => {
    const collectedClaims: Record<string, ITestClaimSlot> = {};
    for (const [key, slot] of Object.entries(slots)) {
        const [medid, qty, days] = key.split(':');
        const override = prescriptionCopayQuantities?.[medid];
        let expectedQty = override?.quantity ?? 30;
        let expectedDays = override?.daysSupply ?? 30;
        if (expectedDays === 90) {
            expectedQty = Math.ceil(expectedQty / 3);
            expectedDays = 30;
        }
        if (Number(qty) === expectedQty && Number(days) === expectedDays) {
            collectedClaims[medid] = { ...slot, copayQuantity: Number(qty), copayDaysSupply: Number(days) };
        }
    }
    const hasMissingCopayAmount = eligiblePrescriptions.some(p => !(String(p.medid) in collectedClaims));
    return { collectedClaims, hasMissingCopayAmount };
};

interface Props {
    navigateToStep?: (step: CustomersStepTypeEnumPaths, index?: number, searchParams?: URLSearchParamsInit) => void;
}

const Review = ({ navigateToStep }: Props) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const posthog = usePostHog();
    const sendDataToGTM = useGTMDispatch();
    const currentUser = useAppSelector(selectOnboardingUser);
    const existingCreditCards = useAppSelector(selectCreditCards);
    const [showCopay, setShowCopay] = useState(false);
    const [completeLoading, setCompleteLoading] = useState(false);
    const [claims, setClaims] = useState<Record<string, ITestClaimSlot>>({});
    const [otcCosts, setOtcCosts] = useState<Record<string, { medid: string; cost: number | null }>>({});
    const userProfile = useAppSelector(selectUserProfileInfo);
    const latestUserOnboarding = currentUser?.userOnboarding;
    const hearAboutUs = useMemo(() => latestUserOnboarding?.details?.hearAboutUs, [latestUserOnboarding]);
    const posthogPrescriptionCount = useMemo(() => latestUserOnboarding?.details?.prescriptionCount, [latestUserOnboarding]);
    const [paymentAddIntentButIncomplete, setPaymentAddIntentButIncomplete] = useState(false);
    const [copayRequestsExhausted, setCopayRequestsExhausted] = useState(false);
    const [addPaymentOpen, setAddPaymentOpen] = useState(false);
    const [hasMissingCopayAmount, setHasMissingCopayAmount] = useState(false);
    const [claimsRunning, setClaimsRunning] = useState(false);
    const claimsRunningRef = useRef(false);

    const initialVals = {
        ...paymentFormInitialValues,
        preferred: true,
        firstName: userProfile?.firstName || '',
        lastName: userProfile?.lastName || '',
        base64EncodedImage: '',
        acceptSubmitting: false
    } as ReviewCompleteForm;

    const alwaysRequiredExtras = {
        acceptSubmitting: Yup.boolean()
            .oneOf([true], ERROR_MESSAGES.REQUIRED_FIELD_MISSING),
        base64EncodedImage: Yup.string()
            .test(
                'base64Required',
                ERROR_MESSAGES.REQUIRED_FIELD_MISSING,
                function (value) {
                    const { acceptSubmitting } = this.parent;
                    if (acceptSubmitting && !value) {
                        return false;
                    }
                    return true;
                }
            ),
    };

    const canRunClaimsBasedOnInsurancesInProfile = useMemo(() => {
        const canRunMoreClaimsRes = canRunMoreClaims(currentUser);
        return canRunMoreClaimsRes;
    }, [currentUser]);

    const runAllClaims = async (skipOtcFetch = false) => {
        if (
            !latestUserOnboarding ||
            !canRunClaimsBasedOnInsurancesInProfile ||
            claimsRunningRef.current
        ) {
            return;
        }

        try {
            claimsRunningRef.current = true;
            setClaimsRunning(true);
            const [slots] = await Promise.all([
                runFullCopayClaims(currentUser),
                ...(!skipOtcFetch ? [fetchOtcPrices()] : [])
            ]);

            if (Object.keys(slots).length) {
                const prescriptionCopayQuantities = (currentUser?.userOnboarding?.details as unknown as { prescriptionCopayQuantities?: Record<string, { quantity: number; daysSupply: number }> })?.prescriptionCopayQuantities;
                const eligiblePrescriptions = getCopayEligiblePrescriptions(currentUser?.transferPrescriptions) as ITestClaimTransferPrescriptions[];
                const { collectedClaims, hasMissingCopayAmount } = slotsByMedid(slots, eligiblePrescriptions, prescriptionCopayQuantities);
                setClaims(collectedClaims);
                setHasMissingCopayAmount(hasMissingCopayAmount);
                setShowCopay(true);
            }
        } catch (err) {
            console.error('runAllClaims failed', err);
        } finally {
            navigate({
                pathname: location.pathname,
                search: `?${new URLSearchParams({ copayTriggered: 'true' })}`,
            });
            setClaimsRunning(false);
            claimsRunningRef.current = false;
        }
    };

    const sendMedboxHueColorToPosthog = useCallback(() => {
        const selectedHueType = currentUser?.userOnboarding?.details?.medboxHueType;
        const hueName = hueOptionsList.find(hue => hue.id === selectedHueType)?.name;
        if (hueName) {
            posthog?.capture(GtmEvent.SELECTED_MEDBOX_COLOR, { color: hueName, hear_about_us: hearAboutUs, prescription_count: posthogPrescriptionCount });
        }
    }, [currentUser?.userOnboarding?.details?.medboxHueType, posthog, hearAboutUs, posthogPrescriptionCount]);


    useEffect(() => {
        if (isCopayRequestLimitReached(currentUser)) {
            setCopayRequestsExhausted(true);
        }
    }, [currentUser?.userOnboarding?.details]);

    const paymentForm = useFormikWrapper<ReviewCompleteForm>({
        initialValues: initialVals,
        validationSchema: Yup.lazy((values) => {
            return Yup.object().shape(alwaysRequiredExtras);
        }),
        onSubmit: async () => {
            if (completeLoading || shouldRequireCardSave) return;
            if (!currentUser) return;
            setCompleteLoading(true);

            try {
                const croppedAndDatedSig = await cropBase64ImageWithLine(paymentForm.values.base64EncodedImage);
                if (!croppedAndDatedSig) {
                    throw new Error('Signature is empty');
                }

                const uploadRes = await dispatch(
                    uploadSignature(croppedAndDatedSig)
                );

                if (uploadRes.meta.requestStatus === 'rejected') {
                    throw new Error('Upload failed');
                }

                const finishRes = await dispatch(CustomersController.onFinishOnboarding());

                if (finishRes.meta.requestStatus === 'rejected') {
                    throw new Error('Onboarding finish failed');
                }

                // will use the onboarding data from onboarding to select the medbox color
                sendMedboxHueColorToPosthog();
                completeOnboardingNotesRequest(currentUser);

                if (currentUser?.userOnboarding) {
                    const payload = cloneDeep(currentUser.userOnboarding);
                    payload.details = {
                        ...payload.details,
                        onboardingCompletionTs: Date.now(),
                        currentStep: CustomersStepTypeEnum.ThankYou,
                    };

                    await dispatch(CustomersController.updateMetadata(payload));

                    sendDataToGTMForPrescriptions();
                    sendDataToGTMForOnboardingCompleted();
                    triggerOnboardingCompletedSfWebhook(currentUser?.email);

                    navigate(ROUTES.THANK_YOU);
                }
            } catch (error) {
                toast.error(ERROR_MESSAGES.DEFAULT);
            } finally {
                setCompleteLoading(false);
            }
        }
    });

    const shouldRequireCardSave = useMemo(() => {
        return existingCreditCards.length === 0
    }, [existingCreditCards]);

    const prescribables = useMemo(() => convertedMedications(currentUser!), [currentUser]);

    const triggerOnboardingCompletedSfWebhook = useCallback(async (email: string) => {
        try {
            await saleForceWebHookInstance.post({
                email,
                phone: '',
                activation: 'activation2'
            });
        } catch (error) {
            console.error('Error in SaleForceWebHook:', error);
        }
    }, []);

    const sendDataToGTMForPrescriptions = useCallback(() => {
        const otcCount =
            prescribables.includingMedbox.filter(item => item.otc).length +
            prescribables.packagedSeparately.filter(item => item.otc).length;
        const prescriptionCount = prescribables.includingMedbox.length + prescribables.packagedSeparately.length - otcCount;
        sendDataToGTM({
            event: GtmEvent.PRESCRIPTION_ADDED,
            userId: currentUser?.userProfile?.accountId,
            prescriptionCount,
            otcCount,
            final: true,
            hear_about_us: hearAboutUs,
            prescription_count: posthogPrescriptionCount,
        });
        posthog?.capture(GtmEvent.PRESCRIPTION_ADDED, {
            hear_about_us: hearAboutUs,
            prescription_count: posthogPrescriptionCount,
        });
    }, [currentUser?.userProfile?.accountId, posthog, prescribables.includingMedbox, prescribables.packagedSeparately, sendDataToGTM, hearAboutUs, posthogPrescriptionCount]);

    const sendDataToGTMForOnboardingCompleted = useCallback(() => {
        sendDataToGTM({ event: GtmEvent.ONBOARDING_COMPLETED, userId: currentUser?.userProfile?.accountId, hear_about_us: hearAboutUs, prescription_count: posthogPrescriptionCount });
        posthog?.capture(GtmEvent.ONBOARDING_COMPLETED, {
            hear_about_us: hearAboutUs,
            prescription_count: posthogPrescriptionCount,
        });

    }, [currentUser?.userProfile?.accountId, posthog, sendDataToGTM, hearAboutUs, posthogPrescriptionCount]);

    const handleSubmit = async () => {
        if (shouldRequireCardSave) {
            setPaymentAddIntentButIncomplete(true);
        }
        paymentForm.handleSubmit();
    };

    const fetchOtcPrices = async () => {
        if (!currentUser) return;
        const otcs = getPriceEligibleOtc(currentUser.transferPrescriptions);
        const otcCosts = await getOtcCostsByQuantity(otcs);
        setOtcCosts(otcCosts.reduce((acc, item) => {
            acc[item.medid] = item;
            return acc;
        }, {} as Record<string, { medid: string; cost: number | null }>));
    }

    const prescriptionCopayQuantities =
        currentUser?.userOnboarding?.details?.prescriptionCopayQuantities;

    const prevRef = useRef<typeof prescriptionCopayQuantities | null>(null);

    useEffect(() => {
        if (!prescriptionCopayQuantities) return;

        if (prevRef.current) {
            const prev = prevRef.current;
            const curr = prescriptionCopayQuantities;

            const hasMeaningfulChange = Object.keys(curr).some(medId => {
                const prevMed = prev[medId];
                const currMed = curr[medId];

                if (!prevMed || !currMed) return false; // ignore new/removal unless you want to count it

                return (
                    prevMed.quantity !== currMed.quantity ||
                    prevMed.daysSupply !== currMed.daysSupply
                );
            });

            if (hasMeaningfulChange) {
                runAllClaims(true);
            }
        }

        // update ref AFTER comparison
        prevRef.current = prescriptionCopayQuantities;
    }, [prescriptionCopayQuantities]);

    const onPaymentFormChange = () => {
        setPaymentAddIntentButIncomplete(false);
    };

    const openPaymentForm = () => {
        setAddPaymentOpen(true);
        setPaymentAddIntentButIncomplete(false);
    }

    const closePaymentForm = () => {
        setAddPaymentOpen(false);
    }

    useEffect(() => {
        if (!latestUserOnboarding ||
            !canRunClaimsBasedOnInsurancesInProfile) {
            return;
        }
        runAllClaims();
    }, [latestUserOnboarding, canRunClaimsBasedOnInsurancesInProfile]);

    if (!currentUser) return null;
    return (
        <>
            <div className="mb-[1.25rem] sm:mb-[1.5625rem] mt-[1.25rem] sm:mt-[2.8125rem] flex justify-center">
                <h2 className="review__heading text-center font-agrandir text-2xl md:text-3xl lg:text-left xl:text-5xl">
                    Let’s Review <span className="text-primary-700">Your MedBox</span>
                </h2>
            </div>
            <_SplitLayout
                customLeftClassNames={['!pl-0', 'xl-max:mb-[1rem]', 'xl-max:w-[700px]']}
                customRightClassNames={['xl-max:w-[700px]']}
                sideContent={
                    <Box
                        bgWhite
                        sx={{
                            gap: '28px',
                            padding: '30px !important',
                            boxShadow: '0px 4px 7.1px 0px #0000000F',
                            flex: '1 1 auto',
                            borderRadius: '20px',
                            maxWidth: '647px',
                            [theme.breakpoints.up('xl')]: {
                                width: 'calc(100% - 1.3125rem)',
                                marginRight: '1.3125rem'
                            },

                            [theme.breakpoints.down('xl')]: {
                                width: '100%',
                                maxWidth: '100%'
                            },

                            [theme.breakpoints.down('sm')]: {
                                padding: '20px !important'
                            },
                            '@media only screen and (min-width: 1441px)': {
                                maxWidth: '700px'
                            }
                        }}
                    >
                        <ReviewMainInfo
                            claims={claims}
                            showCopayLoading={claimsRunning}
                            copayRequestsExhausted={copayRequestsExhausted}
                            canRunClaimsBasedOnInsurancesInProfile={canRunClaimsBasedOnInsurancesInProfile}
                            navigateToStep={navigateToStep}
                            prescribables={prescribables}
                            showCopay={showCopay}
                            hasMissingCopayAmount={hasMissingCopayAmount}
                        />
                        <ReviewMedicationList
                            showCopay={Object.keys(claims).length > 0 && canRunClaimsBasedOnInsurancesInProfile}
                            copayRequestsExhausted={copayRequestsExhausted}
                            claimsRunning={claimsRunning}
                            claims={claims}
                            otcCosts={otcCosts}
                            prescribables={prescribables}
                            navigateToStep={navigateToStep}
                            onQuantityChange={_.noop}
                            canRunClaimsBasedOnInsurancesInProfile={canRunClaimsBasedOnInsurancesInProfile}
                        />
                        <ReviewPayment
                            paymentMethodError={paymentAddIntentButIncomplete}
                            formikControl={paymentForm}
                            onPaymentFormChange={onPaymentFormChange}
                            addPaymentOpen={addPaymentOpen}
                            openPaymentForm={openPaymentForm}
                            closePaymentForm={closePaymentForm}
                        />
                    </Box>
                }
                mainContent={<ReviewSubmit completeLoading={completeLoading} onSubmit={handleSubmit} formikControl={paymentForm} />}
            />
        </>
    );
};

export default withScrollRestoration(Review);
